import { motion } from "framer-motion";
import {
  ArrowRight, ArrowUpRight, QrCode, Check, ChevronRight,
  Users, Package, Building2, Cpu, Sparkles,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { LOGO_URL } from "../components/Logo";
import { useLang } from "../context/LanguageContext";

const TSHIRT_IMG = "/connected-words-shirt.png";

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
};

function Overline({ children, light }) {
  return (
    <p className={`text-xs font-bold uppercase tracking-[0.22em] mb-4 ${light ? "text-u-purple" : "text-u-purple"}`}>
      {children}
    </p>
  );
}

function Flow({ steps, accent }) {
  return (
    <div className="flex flex-wrap items-center gap-2 sm:gap-3">
      {steps.map((s, i) => (
        <span key={s} className="flex items-center gap-2 sm:gap-3">
          <span
            className={`rounded-2xl px-4 sm:px-5 py-2.5 font-display font-bold text-sm sm:text-lg tracking-tight ${
              accent
                ? "bg-u-purple text-white shadow-[0_10px_30px_-8px_rgba(139,92,246,0.6)]"
                : "bg-white/[0.06] text-white/80 border border-white/10"
            }`}
          >
            {s}
          </span>
          {i < steps.length - 1 && (
            <ChevronRight size={20} className={accent ? "text-u-purple" : "text-white/30"} />
          )}
        </span>
      ))}
    </div>
  );
}

export default function Home() {
  const { t } = useLang();
  const visionIcons = [Users, Package, Building2, Cpu];

  return (
    <div className="App overflow-x-hidden bg-u-bg text-u-ink">
      <Navbar />

      {/* ═══════════ 01 · HERO ═══════════ */}
      <section className="relative min-h-[92vh] flex items-center pt-32 pb-24 overflow-hidden" data-testid="hero-section">
        <div className="mesh-glow w-[620px] h-[620px] bg-u-purple/25 -top-40 -left-32" />
        <div className="mesh-glow w-[520px] h-[520px] bg-blue-400/20 top-10 right-0" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_-10%,rgba(139,92,246,0.10),transparent_55%)]" />

        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-center w-full">
          <motion.div
            className="lg:col-span-7"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-1.5 text-xs font-semibold text-slate-600 mb-7 shadow-sm">
              <Sparkles size={13} className="text-u-purple" /> {t.hero.badge}
            </div>
            <h1 className="font-display font-black tracking-tight text-5xl sm:text-6xl lg:text-7xl leading-[1.02] text-u-ink">
              {t.hero.title1}
              <br />
              <span className="bg-gradient-to-r from-u-ink via-u-navy to-u-purple bg-clip-text text-transparent">
                {t.hero.title2}
              </span>
            </h1>
            <p className="mt-7 max-w-xl text-lg text-slate-500 leading-relaxed font-medium">
              {t.hero.subtitle}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <a
                href="https://use1id-prd.vercel.app/r/register"
                data-testid="hero-cta-primary"
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-u-purple px-8 py-4 text-base font-semibold text-white hover:bg-u-purple/90 transition-colors shadow-[0_16px_40px_-12px_rgba(139,92,246,0.6)]"
              >
                {t.hero.ctaPrimary}
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="#proof"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-8 py-4 text-base font-medium text-u-ink hover:bg-[#ECEDF8] transition-colors"
              >
                {t.hero.ctaSecondary}
              </a>
            </div>
          </motion.div>

          <motion.div
            className="lg:col-span-5 flex justify-center"
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.15 }}
          >
            <motion.div
              className="relative"
              animate={{ y: [-10, 10] }}
              transition={{ repeat: Infinity, repeatType: "reverse", duration: 4, ease: "easeInOut" }}
            >
              <div className="absolute inset-0 -m-16 bg-u-purple/20 blur-[100px] rounded-full" />
              <img
                src="/1id-model-3d.png"
                alt="1iD architecture model"
                data-testid="hero-model-visual"
                className="relative w-full max-w-[440px] object-contain drop-shadow-[0_30px_70px_rgba(11,18,33,0.25)]"
              />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════ 02 · THE PROBLEM ═══════════ */}
      <section id="problem" className="relative py-24 md:py-32 bg-white" data-testid="problem-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <motion.div {...fadeUp} className="max-w-2xl mb-14">
            <Overline>{t.problem.overline}</Overline>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-8 items-stretch">
            {/* TODAY */}
            <motion.div
              {...fadeUp}
              className="rounded-[28px] bg-slate-50 border border-slate-200 p-8 md:p-10 flex flex-col"
              data-testid="problem-today-card"
            >
              <span className="text-xs font-bold uppercase tracking-[0.22em] text-slate-400 mb-5">
                {t.problem.todayLabel}
              </span>
              <h3 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight leading-[1.1] text-slate-700">
                {t.problem.todayHeadline}
              </h3>
              <p className="mt-5 text-slate-500 leading-relaxed font-medium">{t.problem.todayBody}</p>
              <div className="mt-auto pt-8">
                <img
                  src="/hub-traditional.png"
                  alt=""
                  aria-hidden="true"
                  className="w-full max-w-sm mx-auto opacity-90 grayscale-[0.2]"
                />
              </div>
            </motion.div>

            {/* WITH 1iD */}
            <motion.div
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.1 }}
              className="relative rounded-[28px] bg-white border border-u-purple/25 p-8 md:p-10 flex flex-col shadow-[0_20px_60px_-20px_rgba(139,92,246,0.35)] overflow-hidden"
              data-testid="problem-onid-card"
            >
              <div className="mesh-glow w-[360px] h-[360px] bg-u-purple/20 -top-24 -right-16" />
              <span className="relative text-xs font-bold uppercase tracking-[0.22em] text-u-purple mb-5">
                {t.problem.oneLabel}
              </span>
              <h3 className="relative font-display font-extrabold text-3xl md:text-4xl tracking-tight leading-[1.1] text-u-ink">
                {t.problem.oneHeadline}
              </h3>
              <p className="relative mt-5 text-slate-500 leading-relaxed font-medium">{t.problem.oneBody}</p>
              <div className="relative mt-auto pt-8">
                <img
                  src="/hub-1id.png"
                  alt=""
                  aria-hidden="true"
                  className="w-full max-w-sm mx-auto drop-shadow-[0_20px_50px_rgba(139,92,246,0.3)]"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══════════ 03 · THE IDEA (inverted model) ═══════════ */}
      <section id="idea" className="relative py-16 md:py-24" data-testid="idea-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <motion.div
            {...fadeUp}
            className="relative rounded-[36px] bg-u-ink p-10 md:p-16 lg:p-20 overflow-hidden"
          >
            <div className="mesh-glow w-[520px] h-[520px] bg-u-purple/40 -bottom-40 -right-20" />
            <div className="mesh-glow w-[420px] h-[420px] bg-blue-500/20 -top-32 -left-10" />

            <div className="relative max-w-4xl">
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-u-purple mb-6">{t.idea.overline}</p>
              <h2 className="font-display font-black text-4xl md:text-6xl tracking-tight leading-[1.05] text-white">
                {t.idea.headline}
              </h2>
            </div>

            <div className="relative mt-12 grid gap-8 lg:grid-cols-2 lg:gap-12">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-white/40 mb-4">{t.idea.tradLabel}</p>
                <Flow steps={t.idea.tradFlow} />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-u-purple mb-4">{t.idea.idLabel}</p>
                <Flow steps={t.idea.idFlow} accent />
              </div>
            </div>

            <motion.p
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.15 }}
              className="relative mt-14 font-display font-bold text-2xl md:text-4xl tracking-tight leading-tight text-white"
            >
              {t.idea.killer.split(".")[0]}.
              <span className="text-u-purple"> {t.idea.killer.split(".").slice(1).join(".").trim()}</span>
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* ═══════════ 04 · PROOF (show, don't explain) ═══════════ */}
      <section id="proof" className="relative py-24 md:py-32 bg-white" data-testid="proof-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-14 lg:gap-16 items-center">
          <motion.div {...fadeUp}>
            <Overline>{t.proof.overline}</Overline>
            <h2 className="font-display font-black text-4xl md:text-5xl tracking-tight leading-[1.08] text-u-ink">
              {t.proof.headline}
            </h2>
            <p className="mt-6 text-lg text-u-purple font-semibold">{t.proof.brand}</p>
            <p className="mt-4 text-lg text-slate-500 leading-relaxed font-medium">{t.proof.body}</p>
            <div className="mt-8 grid grid-cols-2 gap-3 max-w-md">
              {t.proof.features.map((f) => (
                <div key={f} className="flex items-center gap-2.5 rounded-xl bg-u-bg border border-slate-200/80 px-4 py-3 text-sm font-medium text-slate-700">
                  <Check size={16} className="text-u-purple shrink-0" /> {f}
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="relative">
            <div className="relative rounded-[32px] overflow-hidden bg-gradient-to-br from-[#ECEDF8] to-[#DAD5F9] border border-slate-200/70 shadow-[0_30px_70px_-25px_rgba(11,18,33,0.4)] p-6 sm:p-10">
              <div className="mesh-glow w-[360px] h-[360px] bg-u-purple/20 -top-20 -right-16" />
              <img
                src={TSHIRT_IMG}
                alt="Connected Words T-shirt — TOGETHER, we turn words into meaning"
                data-testid="proof-tshirt-image"
                className="relative w-full max-w-[420px] mx-auto object-contain drop-shadow-[0_25px_50px_rgba(11,18,33,0.25)]"
              />
              {/* glass passport chip — text stays HTML/translatable */}
              <div className="absolute bottom-5 left-5 right-5 rounded-2xl backdrop-blur-xl bg-white/85 border border-white/60 p-5 shadow-lg">
                <div className="flex items-center gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-u-ink text-white shrink-0">
                    <QrCode size={22} />
                  </span>
                  <div className="min-w-0">
                    <p className="font-display font-semibold text-u-ink leading-tight">Connected Words</p>
                    <p className="text-xs text-slate-500">{t.proof.passportLabel}</p>
                  </div>
                  <span className="ml-auto flex items-center gap-1.5 rounded-full bg-emerald-500/10 px-3 py-1 text-xs font-semibold text-emerald-600">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Live
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════ 05 · THE VISION (ecosystem) ═══════════ */}
      <section id="vision" className="relative py-24 md:py-32" data-testid="vision-section">
        <div className="mesh-glow w-[600px] h-[600px] bg-u-purple/12 top-10 left-1/2 -translate-x-1/2" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <motion.div {...fadeUp} className="max-w-3xl">
            <Overline>{t.vision.overline}</Overline>
            <h2 className="font-display font-black text-4xl md:text-5xl tracking-tight leading-[1.08] text-u-ink">
              {t.vision.headline}
            </h2>
          </motion.div>

          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.08 }} className="mt-8">
            <span className="inline-flex items-center gap-2 rounded-full bg-u-ink px-5 py-2.5 text-sm font-bold tracking-tight text-white shadow-[0_12px_30px_-10px_rgba(11,18,33,0.5)]">
              <Cpu size={15} className="text-u-purple" /> {t.vision.aiPill}
            </span>
          </motion.div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-6">
            {t.vision.items.map((item, i) => {
              const Icon = visionIcons[i];
              const wide = i === 0 || i === 3;
              return (
                <motion.div
                  key={item.title}
                  {...fadeUp}
                  transition={{ ...fadeUp.transition, delay: 0.05 * i }}
                  className={`group rounded-[28px] card p-8 flex flex-col justify-between hover:-translate-y-1 hover:border-u-purple/30 transition-[transform,border-color] ${
                    wide ? "md:col-span-7" : "md:col-span-5"
                  }`}
                  data-testid={`vision-item-${i}`}
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-u-purple/10 text-u-purple mb-8">
                    <Icon size={24} strokeWidth={1.6} />
                  </span>
                  <div className="flex items-end justify-between gap-4">
                    <div>
                      <h3 className="font-display font-bold text-2xl tracking-tight text-u-ink">{item.title}</h3>
                      <p className="mt-1 text-slate-500 font-medium">{item.desc}</p>
                    </div>
                    <ArrowUpRight size={22} className="text-slate-300 group-hover:text-u-purple transition-colors shrink-0" />
                  </div>
                </motion.div>
              );
            })}
          </div>

          <motion.div {...fadeUp} className="mt-16 text-center">
            <h3 className="font-display font-black text-3xl md:text-5xl tracking-tight text-u-ink">
              {t.vision.footer}
            </h3>
            <a
              href="/investor.html"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="vision-explore-btn"
              className="group mt-8 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-7 py-3.5 text-base font-semibold text-u-ink hover:border-u-purple/40 hover:text-u-purple transition-colors"
            >
              {t.vision.explore}
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* ═══════════ 06 · TWO CTA PATHS ═══════════ */}
      <section className="relative py-16 md:py-24" data-testid="cta-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {/* FOR PEOPLE */}
            <motion.div
              {...fadeUp}
              className="relative rounded-[32px] bg-white border border-u-purple/25 p-10 md:p-12 overflow-hidden shadow-[0_20px_60px_-24px_rgba(139,92,246,0.35)] flex flex-col"
              data-testid="cta-people-card"
            >
              <div className="mesh-glow w-[320px] h-[320px] bg-u-purple/15 -top-20 -right-10" />
              <span className="relative flex h-11 w-11 items-center justify-center rounded-2xl bg-u-purple/10 text-u-purple mb-7">
                <Users size={22} />
              </span>
              <p className="relative text-xs font-bold uppercase tracking-[0.22em] text-u-purple mb-3">{t.cta.peopleLabel}</p>
              <h3 className="relative font-display font-bold text-3xl md:text-4xl tracking-tight leading-tight text-u-ink">
                {t.cta.peopleHeadline}
              </h3>
              <a
                href="https://use1id-prd.vercel.app/r/register"
                data-testid="cta-people-btn"
                className="group relative mt-8 inline-flex items-center gap-2 rounded-full bg-u-purple px-8 py-4 text-base font-semibold text-white hover:bg-u-purple/90 transition-colors w-fit shadow-[0_14px_36px_-12px_rgba(139,92,246,0.6)]"
              >
                {t.cta.peopleCta}
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </a>
            </motion.div>

            {/* FOR INVESTORS & PARTNERS */}
            <motion.div
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.1 }}
              className="relative rounded-[32px] bg-u-ink p-10 md:p-12 overflow-hidden shadow-2xl flex flex-col"
              data-testid="cta-investors-card"
            >
              <div className="mesh-glow w-[360px] h-[360px] bg-u-purple/40 -bottom-24 -right-10" />
              <span className="relative flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10 text-u-purple mb-7">
                <Building2 size={22} />
              </span>
              <p className="relative text-xs font-bold uppercase tracking-[0.22em] text-u-purple mb-3">{t.cta.investorsLabel}</p>
              <h3 className="relative font-display font-bold text-3xl md:text-4xl tracking-tight leading-tight text-white">
                {t.cta.investorsHeadline}
              </h3>
              <a
                href="/investor.html"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-investors-btn"
                className="group relative mt-8 inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-base font-semibold text-u-ink hover:bg-white/90 transition-colors w-fit"
              >
                {t.cta.investorsCta}
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </a>
            </motion.div>
          </div>

          <motion.p
            {...fadeUp}
            className="mt-14 text-center font-display font-bold text-xl md:text-3xl tracking-tight text-slate-400"
            data-testid="cta-privacy-line"
          >
            {t.cta.privacy}
          </motion.p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
